// Lib
import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useEffect, useState } from 'react';
import { omit } from 'lodash';

// Utils
import { getTemplateIsCustom } from '../boardTemplatePickerUtil';
import { sendAmplitudeEvent } from '../../../analytics/amplitudeService';
import { fetchBoard, fetchVisibleDescendantBoards } from '../../../element/board/boardService';
import { getElement } from '../../../element/selectors/elementSelector';

// Actions
import { visibleBoardIdSet } from '../../../reducers/currentBoardId/currentBoardIdActions';

// Constants
import { AMPLITUDE_USER_PROPS, TRACKED_FEATURES } from '../../../../common/analytics/statsConstants';
import { TemplatesMap } from '../templateTypes';

type Props = {
    templates: TemplatesMap;
    enabled: boolean;
};

const useSelectTemplate = ({
    templates,
    enabled,
}: Props): {
    selectedTemplateId: string | null;
    selectTemplate: (templateId: string | null) => Promise<void>;
    loadingTemplateId: string | null;
    templateFetchErrors: Record<string, string>;
} => {
    const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
    const [loadingTemplateId, setIsLoadingTemplateId] = useState<string | null>(null);
    const [templateFetchErrors, setTemplateFetchErrors] = useState<Record<string, string>>({});

    const selectedTemplateBoardId = selectedTemplateId ? templates[selectedTemplateId]?.templateBoardId : null;
    const selectedTemplateBoard =
        useSelector((state) => getElement(state, { elementId: selectedTemplateBoardId })) || null;

    const removeTemplateFetchError = (templateId: string) => {
        setTemplateFetchErrors((prevErrors) => omit(prevErrors, templateId));
    };

    const temporarilyAddTemplateFetchError = (templateId: string, errorMessage: string) => {
        setTemplateFetchErrors((prevErrors) => ({
            ...prevErrors,
            [templateId]: errorMessage,
        }));

        setTimeout(() => removeTemplateFetchError(templateId), 5000);
    };

    const dispatch = useDispatch();

    const fetchTemplate = async (templateBoardId: string, templateId: string) => {
        setIsLoadingTemplateId(templateId);

        removeTemplateFetchError(templateId);

        const response = await dispatch(
            fetchBoard({
                boardId: templateBoardId,
                addLoadedBoardHierarchies: false,
                loadAncestors: false,
            }),
        );

        setIsLoadingTemplateId(null);

        // @ts-ignore - dispatch promise returns the wrong type
        const error = response?.errors?.[templateBoardId]?.error;
        if (error) {
            temporarilyAddTemplateFetchError(templateId, error.message);
            return;
        }

        dispatch(fetchVisibleDescendantBoards(templateBoardId));
    };

    const selectTemplate = useCallback(
        async (templateId: string | null) => {
            const templateBoardId = templateId ? templates[templateId]?.templateBoardId : null;

            if (!templateId || !templateBoardId) {
                setSelectedTemplateId(null);
                return;
            }

            await fetchTemplate(templateBoardId, templateId);

            setSelectedTemplateId(templateId);
        },
        [templates],
    );

    useEffect(() => {
        if (!enabled || !selectedTemplateBoard || !selectedTemplateId) {
            setSelectedTemplateId(null);
            dispatch(visibleBoardIdSet({ boardId: null }));
            return;
        }

        dispatch(visibleBoardIdSet({ boardId: selectedTemplateBoardId }));

        const isCustomTemplate = getTemplateIsCustom(templates[selectedTemplateId]);

        sendAmplitudeEvent({
            eventType: 'Viewed template',
            eventProperties: {
                templateId: isCustomTemplate ? 'custom' : selectedTemplateId,
            },
            userProperties: {
                [AMPLITUDE_USER_PROPS.FEATURE]: { [TRACKED_FEATURES.TEMPLATE_PICKER]: true },
            },
        });
    }, [selectedTemplateBoard]);

    return { selectedTemplateId, selectTemplate, loadingTemplateId, templateFetchErrors };
};

export default useSelectTemplate;
